import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { authAdapter } from '@/api/authAdapter';
import { API_BASE_URL as BASE } from '@/api/apiConfig';

const API_BASE_URL = `${BASE}/api`;

const oauthCallbackRequestMap = new Map<string, Promise<any>>();

const dedupeOAuthCallbackRequest = (
  key: string,
  requester: () => Promise<any>
): Promise<any> => {
  const existingRequest = oauthCallbackRequestMap.get(key);
  if (existingRequest) {
    return existingRequest;
  }

  const nextRequest = requester().finally(() => {
    oauthCallbackRequestMap.delete(key);
  });

  oauthCallbackRequestMap.set(key, nextRequest);
  return nextRequest;
};

// 디바이스 정보 가져오기
const getDeviceInfo = (): string => {
  return navigator.userAgent;
};

type AuthError = Error & {
  code?: string;
  status?: number;
  data?: any;
};

const extractAuthErrorCode = (responseData: any, fallbackCode?: string): string | undefined => {
  // B/E 표준 에러 응답: { success, message, errorType, errorCode, timestamp }
  // errorType을 우선 확인, 그 다음 code, error 필드 순서로 확인
  const candidate =
    responseData?.errorType ||
    responseData?.errorCode ||
    responseData?.code ||
    responseData?.error ||
    responseData?.data?.errorType ||
    responseData?.data?.code ||
    fallbackCode;

  if (typeof candidate !== 'string') {
    return undefined;
  }

  const normalized = candidate.trim();
  return normalized || undefined;
};

const createAuthError = (
  message: string,
  options: { code?: string; status?: number; data?: any } = {}
): AuthError => {
  const nextError = new Error(message) as AuthError;
  nextError.code = options.code;
  nextError.status = options.status;
  nextError.data = options.data;
  return nextError;
};

const isHtmlResponseBody = (value: string): boolean => {
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith('<!doctype') || trimmed.startsWith('<html');
};

const extractAuthErrorMessage = (responseData: any): string | undefined => {
  if (typeof responseData === 'string') {
    const trimmed = responseData.trim();
    if (!trimmed || isHtmlResponseBody(trimmed)) {
      return undefined;
    }
    return trimmed;
  }

  const candidate =
    responseData?.message ||
    responseData?.error?.message ||
    responseData?.data?.message;

  if (typeof candidate !== 'string') {
    return undefined;
  }

  const normalized = candidate.trim();
  return normalized || undefined;
};

const isGenericStatusErrorMessage = (message: string): boolean => {
  return /^Request failed with status code \d{3}$/i.test(message.trim());
};

const formatRetryAfterMessage = (retryAfterValue?: string): string | null => {
  if (!retryAfterValue) return null;

  const seconds = Number(retryAfterValue);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;

  if (seconds >= 60) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes}분 후 다시 시도해 주세요.`;
  }

  return `${seconds}초 후 다시 시도해 주세요.`;
};

export interface TeamSummary {
  teamId: number;
  teamName: string;
  myRole: 'OWNER' | 'ADMIN' | 'MEMBER';
  memberCount?: number;
  plan?: string;
  monthlyLimit?: number;
  status?: string;
}

export interface User {
  userId: string;
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  businessNumber?: string;
  userRole: 'SUPER_ADMIN' | 'CLIENT';
  approvalStatus: 'APPROVED' | 'PENDING' | 'REJECTED';
  approvalReason?: string;
  subscriptionPlan: 'FREE' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  preferredLanguage: 'ko' | 'en';
  mustChangePassword: boolean;
  loginType: 'LOCAL' | 'GOOGLE' | 'NAVER' | 'KAKAO';
  lastLoginAt?: string;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  showTimeoutWarning: boolean;
  remainingTime: number;
  activeSessions: SessionInfo[];
  activeTeamId: number | null;
  myTeams: TeamSummary[];
  setActiveTeamId: (id: number | null) => void;
  setMyTeams: (teams: TeamSummary[]) => void;
  login: (
    userId: string,
    password: string,
    rememberMe?: boolean,
    loginType?: 'local' | 'google' | 'naver' | 'kakao',
    consentData?: { agreeTerms?: boolean; agreePrivacy?: boolean; agreeMarketing?: boolean }
  ) => Promise<{
    duplicateLogin?: boolean;
    existingSessions?: SessionInfo[];
  }>;
  completeKakaoSignup: (payload: { kakaoId: string; email: string; name?: string }) => Promise<void>;
  forceLogin: (userId: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  signup: (signupData: SignupData) => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  checkAuth: () => Promise<void>;
  extendSession: () => Promise<void>;
  fetchActiveSessions: () => Promise<void>;
  terminateSession: (sessionId: string) => Promise<void>;
}

interface SessionInfo {
  sessionId: string;
  deviceInfo: string;
  ipAddress: string;
  loginTime: string;
  lastActivityTime: string;
  isCurrent: boolean;
}

interface SignupData {
  userId: string;
  password: string;
  name: string;
  email: string;
  phone?: string;
  preferredLanguage: 'ko' | 'en';
  agreeTerms: boolean;
  agreePrivacy: boolean;
  agreeMarketing?: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [activeSessions, setActiveSessions] = useState<SessionInfo[]>([]);
  const [activeTeamId, setActiveTeamIdState] = useState<number | null>(() => {
    const stored = sessionStorage.getItem('activeTeamId');
    return stored ? parseInt(stored, 10) : null;
  });
  const [myTeams, setMyTeamsState] = useState<TeamSummary[]>([]);

  const setActiveTeamId = (id: number | null) => {
    setActiveTeamIdState(id);
    if (id === null) {
      sessionStorage.removeItem('activeTeamId');
    } else {
      sessionStorage.setItem('activeTeamId', String(id));
    }
  };

  const setMyTeams = (teams: TeamSummary[]) => {
    setMyTeamsState(teams);
  };
  
  // 세션 타임아웃 설정 (30분 = 1800초)
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30분 (밀리초)
  const WARNING_TIME = 2 * 60 * 1000; // 2분 전 경고 (밀리초)
  
  const lastActivityRef = React.useRef<number>(Date.now());
  const timeoutTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // 마지막 활동 시간 업데이트
  const updateLastActivity = React.useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', Date.now().toString());
  }, []);

  // 세션 타임아웃 타이머 시작
  const startSessionTimer = React.useCallback(() => {
    // 기존 타이머 제거
    if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    
    // 경고 타이머 (28분 후)
    warningTimerRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setRemainingTime(120); // 2분 남음
      
      // 카운트다운
      const countdownInterval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_TIME);
    
    // 자동 로그아웃 타이머 (30분 후)
    timeoutTimerRef.current = setTimeout(() => {
      handleLogout();
      alert('세션이 만료되어 자동으로 로그아웃되었습니다.');
      window.location.href = '/login?reason=timeout';
    }, SESSION_TIMEOUT);
  }, [SESSION_TIMEOUT, WARNING_TIME]);

  // 세션 연장
  const extendSession = React.useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!currentToken) return;

      // 백엔드 세션 연장 API 호출
      await axios.post(`${API_BASE_URL}/auth/sessions/extend`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
    } catch (error) {
      console.error('Failed to extend session:', error);
    } finally {
      // API 성공/실패 무관하게 로컬 타이머는 항상 리셋
      updateLastActivity();
      setShowTimeoutWarning(false);
      setRemainingTime(0);
      startSessionTimer();
    }
  }, [updateLastActivity, startSessionTimer]);

  // 활성 세션 조회
  const fetchActiveSessions = React.useCallback(async () => {
    try {
      const currentToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!currentToken) return;

      const response = await axios.get(`${API_BASE_URL}/auth/sessions`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      if (response.data.success) {
        setActiveSessions(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch active sessions:', error);
    }
  }, []);

  // 특정 세션 종료
  const terminateSession = React.useCallback(async (sessionId: string) => {
    try {
      const currentToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!currentToken) return;

      const response = await axios.delete(`${API_BASE_URL}/auth/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });

      if (response.data.success) {
        await fetchActiveSessions();
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '세션 종료에 실패했습니다.');
    }
  }, [fetchActiveSessions]);

  // 사용자 활동 감지
  useEffect(() => {
    const isAuth = !!user && !!token;
    if (!isAuth) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      const now = Date.now();
      const lastActivity = lastActivityRef.current;
      
      // 1분 이상 간격이 있을 때만 업데이트
      if (now - lastActivity > 60000) {
        updateLastActivity();
        extendSession();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [user, token, updateLastActivity, extendSession]);

  // 인증된 사용자의 세션 타이머 시작
  useEffect(() => {
    const isAuth = !!user && !!token;
    if (isAuth) {
      updateLastActivity();
      startSessionTimer();
    }

    return () => {
      if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [user, token, startSessionTimer, updateLastActivity]);

  // 페이지 로드 시 토큰 확인
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Failed to parse stored user:', error);
          localStorage.removeItem('authUser');
        }
      }
      checkAuth();
    } else {
      setIsLoading(false);
    }
  }, []);

  // axios interceptor 설정 - API 호출 시 세션 타이머 리셋
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // 성공적인 API 호출 시 타이머 리셋
        const isAuth = !!user && !!token;
        if (isAuth) {
          const now = Date.now();
          const lastActivity = lastActivityRef.current;
          
          // 1분 이상 간격이 있을 때만 타이머 리셋
          if (now - lastActivity > 60000) {
            updateLastActivity();
            // 타이머 재시작
            if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            startSessionTimer();
          }
        }
        return response;
      },
      (error) => {
        // 401 에러 시 자동 로그아웃 (단, 로그인/인증 API 자체의 401은 제외)
        if (error.response?.status === 401) {
          const requestUrl = error.config?.url || '';
          const isAuthEndpoint = requestUrl.includes('/api/auth/login') ||
            requestUrl.includes('/api/auth/google') ||
            requestUrl.includes('/api/auth/token') ||
            requestUrl.includes('/api/auth/naver/callback') ||
            requestUrl.includes('/api/auth/kakao/callback') ||
            requestUrl.includes('/api/auth/kakao/complete-signup') ||
            requestUrl.includes('/api/auth/email-verification/send') ||
            requestUrl.includes('/api/auth/email-verification/verify');
          if (!isAuthEndpoint) {
            handleLogout();
            window.location.href = '/login?reason=unauthorized';
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [user, token, updateLastActivity, startSessionTimer]);

  // 로그인 후 비회원 진단 결과 자동 귀속
  // 어떤 로그인 경로(로컬/구글/네이버/카카오)로든 인증이 완료되면 실행
  useEffect(() => {
    if (!user || !token) return;
    const pendingClaim = sessionStorage.getItem('diagnose_pending_claim');
    if (!pendingClaim) return;
    try {
      const { jobId, guestToken } = JSON.parse(pendingClaim);
      sessionStorage.removeItem('diagnose_pending_claim');
      axios.post(`${API_BASE_URL}/diagnose/claim`, { jobId, guestToken }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => { /* 귀속 실패 무시 (이미 귀속된 경우 등) */ });
    } catch {
      sessionStorage.removeItem('diagnose_pending_claim');
    }
  }, [user, token]);

  // 현재 사용자 정보 확인
  const checkAuth = async () => {
    try {
      const currentToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (!currentToken) {
        handleLogout();
        return;
      }
      const response = await authAdapter.getCurrentUser(currentToken);
      if (response.success && response.data) {
        setUser(response.data);
        localStorage.setItem('authUser', JSON.stringify(response.data));
      } else {
        handleLogout();
      }
    } catch (error) {
      handleLogout();
    } finally {
      setIsLoading(false);
    }
  };

  // 로그인
  const applyAuthenticatedSession = React.useCallback(
    (authData: any, rememberMe: boolean) => {
      const userData = authData?.user as User | undefined;
      const authToken = authData?.token as string | undefined;
      const sessionId = authData?.sessionId as string | undefined;

      if (!authToken || !userData) {
        throw createAuthError('로그인 응답 형식이 올바르지 않습니다.');
      }

      if (rememberMe) {
        localStorage.setItem('authToken', authToken);
      } else {
        sessionStorage.setItem('authToken', authToken);
      }

      if (sessionId) {
        localStorage.setItem('sessionId', sessionId);
      }

      setToken(authToken);
      setUser(userData);
      localStorage.setItem('authUser', JSON.stringify(userData));

      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      updateLastActivity();
      startSessionTimer();

      if (userData.mustChangePassword) {
        window.location.href = '/change-password?forced=true';
      }
    },
    [startSessionTimer, updateLastActivity]
  );

  const login = async (
    userId: string,
    password: string,
    rememberMe: boolean = false,
    loginType: 'local' | 'google' | 'naver' | 'kakao' = 'local',
    consentData?: { agreeTerms?: boolean; agreePrivacy?: boolean; agreeMarketing?: boolean }
  ): Promise<{ duplicateLogin?: boolean; existingSessions?: SessionInfo[] }> => {
    try {
      let response;
      
      if (loginType === 'google') {
        // Google 로그인 - userId는 credential token
        response = await axios.post(`${API_BASE_URL}/auth/google`, {
          credential: userId,
          ...(consentData ?? {}),
        });
      } else if (loginType === 'naver') {
        // 네이버 로그인 - userId는 인증 코드, password는 state
        const requestKey = `naver:${userId}:${password}`;
        response = await dedupeOAuthCallbackRequest(requestKey, () =>
          axios.post(`${API_BASE_URL}/auth/naver/callback`, {
            code: userId,
            state: password,
            ...(consentData ?? {}),
          })
        );
      } else if (loginType === 'kakao') {
        // 카카오 로그인 - userId는 인증 코드
        const requestKey = `kakao:${userId}`;
        response = await dedupeOAuthCallbackRequest(requestKey, () =>
          axios.post(`${API_BASE_URL}/auth/kakao/callback`, {
            code: userId,
            ...(consentData ?? {}),
          })
        );
      } else {
        // 일반 로그인
        response = await axios.post(`${API_BASE_URL}/auth/login`, {
          userId,
          password,
          rememberMe,
          deviceInfo: getDeviceInfo(),
        });
      }

      // 백엔드 API 스펙에 따른 중복 로그인 감지
      if (response.data.duplicateLogin === true) {
        // 중복 로그인 감지 - 호출자(Login 컴포넌트)에서 처리하도록 반환
        return {
          duplicateLogin: true,
          existingSessions: response.data.existingSessions || [],
        };
      }

      // 정상 로그인 처리
      if (response.data.success) {
        applyAuthenticatedSession(response.data.data, rememberMe);

        return { duplicateLogin: false };
      } else {
        const msg = response.data.message || '';
        const isSocialPending =
          response.status === 202 ||
          msg.startsWith('SOCIAL_SIGNUP_PENDING|') ||
          response.data?.errorType === 'SOCIAL_SIGNUP_PENDING';
        throw createAuthError(msg || '로그인에 실패했습니다.', {
          code: isSocialPending ? 'SOCIAL_SIGNUP_PENDING' : extractAuthErrorCode(response.data),
          status: response.status,
          data: response.data,
        });
      }
    } catch (error: any) {
      const responseData = error?.response?.data ?? error?.data;
      const responseStatus = error?.response?.status ?? error?.status;
      const extractedMessage = extractAuthErrorMessage(responseData);
      let errorMessage = extractedMessage || error?.message || '로그인에 실패했습니다.';

      if (loginType === 'local' && responseStatus === 429) {
        const retryAfterMessage = formatRetryAfterMessage(error?.response?.headers?.['retry-after']);
        errorMessage = retryAfterMessage
          ? `로그인 시도가 너무 많습니다. ${retryAfterMessage}`
          : '로그인 시도가 너무 많습니다. 잠시 후 다시 시도해 주세요.';
      }

      if ((loginType === 'google' || loginType === 'naver' || loginType === 'kakao') && isGenericStatusErrorMessage(errorMessage)) {
        if (responseStatus === 401) {
          errorMessage = '소셜 로그인에 실패했습니다. 탈퇴한 계정은 탈퇴일로부터 30일 후 재가입 가능합니다.';
        } else {
          errorMessage = '소셜 로그인 처리 중 오류가 발생했습니다.';
        }
      }

      if (loginType !== 'local') {
        sessionStorage.setItem('socialLoginError', errorMessage);
      }

      throw createAuthError(errorMessage, {
        code: extractAuthErrorCode(responseData, error.code),
        status: responseStatus,
        data: responseData,
      });
    }
  };

  const completeKakaoSignup = async (payload: { kakaoId: string; email: string; name?: string }) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/kakao/complete-signup`, {
        kakaoId: payload.kakaoId,
        email: payload.email,
        name: payload.name || '카카오사용자',
      });

      if (response.data?.success) {
        applyAuthenticatedSession(response.data.data, false);
        return;
      }

      throw createAuthError(response.data?.message || '카카오 회원가입 완료 처리에 실패했습니다.', {
        code: extractAuthErrorCode(response.data),
        status: response.status,
        data: response.data,
      });
    } catch (error: any) {
      if (error?.data || error?.status || error?.code) {
        throw error;
      }

      const responseData = error.response?.data;
      const errorMessage = responseData?.message || error.message || '카카오 회원가입 완료 처리에 실패했습니다.';
      throw createAuthError(errorMessage, {
        code: extractAuthErrorCode(responseData, error.code),
        status: error.response?.status,
        data: responseData,
      });
    }
  };

  // 로그아웃
  const logout = async () => {
    try {
      const currentToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      if (currentToken) {
        // 백엔드 세션 종료
        await axios.post(`${API_BASE_URL}/auth/sessions/logout`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      handleLogout();
    }
  };

  // 강제 로그인 (중복 로그인 시)
  const forceLogin = async (
    userId: string,
    password: string,
    rememberMe: boolean = false
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login/force`, {
        userId,
        password,
        rememberMe,
        deviceInfo: getDeviceInfo(),
      });

      if (response.data.success) {
        applyAuthenticatedSession(response.data.data, rememberMe);
      } else {
        throw new Error(response.data.message || '강제 로그인에 실패했습니다.');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || '강제 로그인에 실패했습니다.';
      throw new Error(errorMessage);
    }
  };

  // 로컬 로그아웃 처리
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('sessionId');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('activeTeamId');
    sessionStorage.removeItem('redirectAfterLogin');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setActiveTeamIdState(null);
    setMyTeamsState([]);
  };

  // 회원가입
  const signup = async (signupData: SignupData) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/signup`, signupData);

      if (!response.data.success) {
        throw new Error(response.data.message || '회원가입에 실패했습니다.');
      }

      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || '회원가입에 실패했습니다.';
      throw new Error(errorMessage);
    }
  };

  // 사용자 정보 업데이트 (로컬 상태만)
  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const nextUser = { ...user, ...userData };
      setUser(nextUser);
      localStorage.setItem('authUser', JSON.stringify(nextUser));
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    showTimeoutWarning,
    remainingTime,
    activeSessions,
    activeTeamId,
    myTeams,
    setActiveTeamId,
    setMyTeams,
    login,
    completeKakaoSignup,
    forceLogin,
    logout,
    signup,
    updateUser,
    checkAuth,
    extendSession,
    fetchActiveSessions,
    terminateSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
